import * as React from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { IOrderRow } from '../../../Entities';
import { ImageEditorPhotoStatus } from '../../entry/queries/use-query-photo-status';
import { canUploadEditedImages, needsNormalImageUpload, UploadVariant } from '../state/utils';
import { ImageDropzone } from './ImageDropzone';
import { ImageSizeSettingsNotice } from './ImageSizeSettingsNotice';

const UploadColumns = styled('div')<{ columns: number }>(({ columns, theme }) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: '16px',
    padding: '16px',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderTop: 'none',
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr'
    }
}));

type UploadEditedImagesSectionProps = {
    orderId: number;
    orderRow: IOrderRow;
    photoStatus: ImageEditorPhotoStatus;
    user: AuthenticatedUser;
    onUploaded: (uploadedFilenames: string[]) => void;
};

export function UploadEditedImagesSection({
    orderId,
    orderRow,
    photoStatus,
    user,
    onUploaded
}: UploadEditedImagesSectionProps) {
    if (!canUploadEditedImages(user, photoStatus)) {
        return null;
    }

    let showNormalUpload = needsNormalImageUpload(user, photoStatus, orderRow);
    let highSettings = photoStatus.highImageSizeSettings;
    let normalSettings = photoStatus.normalImageSizeSettings;

    return (
        <UploadColumns columns={showNormalUpload ? 2 : 1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {highSettings && (
                    <ImageSizeSettingsNotice
                        variant={UploadVariant.Edited}
                        size={highSettings.size}
                        dpi={highSettings.dpi}
                    />
                )}
                <ImageDropzone
                    orderId={orderId}
                    orderRowId={orderRow.id}
                    orderRowFileNames={orderRow.fileNames}
                    variant={UploadVariant.Edited}
                    warnAboutUnknownFilenames={!photoStatus.photographerIsEditor}
                    user={user}
                    onUploaded={onUploaded}
                />
            </Box>
            {showNormalUpload && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <ImageSizeSettingsNotice
                        variant={UploadVariant.Normal}
                        size={normalSettings.size}
                        dpi={normalSettings.dpi}
                    />
                    <ImageDropzone
                        orderId={orderId}
                        orderRowId={orderRow.id}
                        orderRowFileNames={orderRow.fileNames}
                        variant={UploadVariant.Normal}
                        warnAboutUnknownFilenames={false}
                        user={user}
                    />
                </Box>
            )}
        </UploadColumns>
    );
}
